/**
 * The only place this app talks to the server.
 *
 * The base URL is relative so the bundle is origin-agnostic: Apache today, an
 * ALB or CloudFront later, with no rebuild.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    options?: ErrorOptions,
  ) {
    super(message, options)
    this.name = 'ApiError'
  }

  /** The session is gone, or was never there. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  /** The GPU is off, or the server is otherwise not able to help right now. */
  get isUnavailable(): boolean {
    return this.status === 503
  }
}

/**
 * The CSRF token for the current session. The server rejects every unsafe
 * method without it. Held in memory only: localStorage would hand it to any
 * injected script.
 */
let csrfToken: string | null = null
let onUnauthenticated: (() => void) | null = null

export function setCsrfToken(token: string | null): void {
  csrfToken = token
}

/**
 * Registered once by the auth provider. Any 401 from anywhere routes through
 * here, so a session that expires while the browser sat open surfaces as the
 * sign-in prompt rather than as a scattering of errors in whatever screen
 * happened to be open.
 */
export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler
}

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

interface RequestOptions extends RequestInit {
  /** Skip the global 401 handler. Used by the sign-in call itself, where a
   *  401 means "wrong password", not "your session ended". */
  silent401?: boolean
}

async function request<T>(path: string, init?: RequestOptions): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase()
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string>) }
  if (init?.body !== undefined && !(init.body instanceof Blob)) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json'
  }
  if (UNSAFE_METHODS.has(method) && csrfToken) {
    headers['X-CSRF-Token'] = csrfToken
  }

  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers,
      credentials: 'same-origin',
    })
  } catch (cause) {
    throw new ApiError('Could not reach the server.', 0, { cause })
  }

  if (response.status === 401 && !init?.silent401) {
    onUnauthenticated?.()
  }

  if (!response.ok) {
    let detail = `Request failed (${response.status})`
    try {
      const body = (await response.json()) as { detail?: unknown }
      if (typeof body.detail === 'string') detail = body.detail
    } catch {
      // Not every error body is JSON; keep the status-based message.
    }
    throw new ApiError(detail, response.status)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

// --------------------------------------------------------------------------
// shapes
// --------------------------------------------------------------------------

export type Role = 'sysadmin' | 'admin'

export interface User {
  id: number
  username: string
  role: Role
  is_active: boolean
  must_change_password: boolean
  created_at: string
  last_login_at: string | null
}

export interface SessionInfo {
  user: User
  csrf_token: string
}

export type PowerState = 'off' | 'starting' | 'booting' | 'online' | 'stopping' | 'unknown'

export interface GpuStatus {
  state: PowerState
  detail: string
  reachable: boolean
  /** Already filtered server-side to poweron/poweroff. */
  available_actions: string[]
  server_state: string | null
  power_control_configured: boolean
  /** ISO-8601, and null unless the GPU is running. */
  running_since: string | null
  /** Null when no rate is configured, so uptime can show without implying it is free. */
  hourly_cost_eur: number | null
  currency: string
}

export interface SeparationEngine {
  name: string
  description: string
  allowed_targets: string[]
  default_targets: string[]
  derived_targets: string[]
  extra_separation_pass_targets: string[]
}

export interface Capabilities {
  default_separation_engine: string
  allowed_models: string[]
  default_models: string[]
  separation_engines: SeparationEngine[]
}

export interface JobFile {
  id: string
  kind: 'original' | 'varied_source' | 'stem' | 'cover'
  name: string
  stem: string | null
  model: string | null
  size: number
  /** Whether an MP3 preview exists yet. One is built on first play if not. */
  has_preview: boolean
}

export type JobState =
  | 'submitting'
  | 'submit_failed'
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'unknown'

export interface Job {
  id: string
  gpu_job_id: string | null
  username: string
  source_filename: string
  state: JobState
  stage: string | null
  error: string | null
  queue_position: number | null
  params: Record<string, unknown>
  created_at: string
  submitted_at: string | null
  started_at: string | null
  finished_at: string | null
  is_terminal: boolean
  results_mirrored: boolean
  results_expired: boolean
  files: JobFile[]
}

export interface Upload {
  id: string
  filename: string
  declared_size: number
  received_bytes: number
  status: 'pending' | 'ready' | 'consumed'
  chunk_size: number
  expires_at: string
  filename_warning: string | null
}

export interface AuditEvent {
  id: number
  action: string
  user_id: number | null
  username: string | null
  ip: string | null
  detail: string | null
  created_at: string
}

/** The hashed module bundle referenced by a document, e.g. index-BzaVDojj.js. */
export function bundleNameIn(html: string): string | null {
  // Only /assets/ names are hashed per build. Vite's dev document points at
  // /src/main.tsx, which never changes and so must not be treated as a version.
  const match = /<script[^>]+src="(\/assets\/[^"]+\.js)"/.exec(html)
  return match?.[1] ?? null
}

export interface AuditPage {
  items: AuditEvent[]
  total: number
}

export interface AuditFilter {
  actions?: string[]
  username?: string
  /** ISO-8601. The server treats a missing offset as UTC. */
  since?: string
  until?: string
  limit?: number
  offset?: number
}

export interface JobOrder {
  separation_engine?: string
  targets?: string[]
  dit_models?: string[]
  /** 0.15-1.0. Below 0.15 the VM refuses the order; see OrderPage. */
  cover_strength?: number
  infer_steps?: number
  /** 0-100. Both alter the source before the model sees it. */
  tone_change?: number
  timing_drift?: number
}

// --------------------------------------------------------------------------
// calls
// --------------------------------------------------------------------------

export const api = {
  login: async (username: string, password: string): Promise<SessionInfo> => {
    const info = await request<SessionInfo>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
      // A 401 here is a wrong password, not an expired session.
      silent401: true,
    })
    setCsrfToken(info.csrf_token)
    return info
  },

  me: async (): Promise<SessionInfo> => {
    const info = await request<SessionInfo>('/auth/me', { silent401: true })
    setCsrfToken(info.csrf_token)
    return info
  },

  logout: async (): Promise<void> => {
    await request<void>('/auth/logout', { method: 'POST' })
    setCsrfToken(null)
  },

  /** Ends every other session for this user. This one survives. */
  revokeOtherSessions: () =>
    request<{ revoked: number }>('/auth/sessions/revoke-others', { method: 'POST' }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/auth/password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),

  gpuStatus: () => request<GpuStatus>('/gpu/status'),
  gpuCapabilities: () => request<Capabilities>('/gpu/capabilities'),
  gpuPowerOn: () => request<GpuStatus>('/gpu/power/on', { method: 'POST' }),
  gpuPowerOff: () => request<GpuStatus>('/gpu/power/off', { method: 'POST' }),

  /**
   * `refresh` asks the server to poll the GPU before answering. With the GPU
   * off that costs a couple of seconds, so an idle page reads the mirror only.
   */
  listJobs: (refresh = true) => request<Job[]>(`/jobs?refresh=${refresh ? 'true' : 'false'}`),
  getJob: (id: string) => request<Job>(`/jobs/${id}`),
  submitJob: (uploadId: string, order: JobOrder = {}) =>
    request<Job>('/jobs', {
      method: 'POST',
      body: JSON.stringify({ upload_id: uploadId, ...order }),
    }),
  cancelJob: (id: string) => request<Job>(`/jobs/${id}/cancel`, { method: 'POST' }),
  deleteJob: (id: string) => request<void>(`/jobs/${id}`, { method: 'DELETE' }),
  fileUrl: (jobId: string, fileId: string) => `${BASE_URL}/jobs/${jobId}/files/${fileId}`,
  /** The MP3. Eight times smaller than the WAV, which is the point of it. */
  previewUrl: (jobId: string, fileId: string) =>
    `${BASE_URL}/jobs/${jobId}/files/${fileId}/preview`,

  startUpload: (filename: string, size: number) =>
    request<Upload>('/uploads', { method: 'POST', body: JSON.stringify({ filename, size }) }),
  uploadChunk: (id: string, offset: number, chunk: Blob) =>
    request<Upload>(`/uploads/${id}/chunk?offset=${offset}`, {
      method: 'PUT',
      body: chunk,
      headers: { 'Content-Type': 'application/octet-stream' },
    }),
  /** Where the server thinks this upload is. Used to resume after a conflict. */
  getUpload: (id: string) => request<Upload>(`/uploads/${id}`),
  completeUpload: (id: string) => request<Upload>(`/uploads/${id}/complete`, { method: 'POST' }),
  abortUpload: (id: string) => request<void>(`/uploads/${id}`, { method: 'DELETE' }),

  listUsers: () => request<User[]>('/users'),
  createUser: (username: string, password: string, role: Role) =>
    request<User>('/users', { method: 'POST', body: JSON.stringify({ username, password, role }) }),
  updateUser: (id: number, changes: { role?: Role; is_active?: boolean }) =>
    request<User>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(changes) }),
  resetUserPassword: (id: number, newPassword: string) =>
    request<void>(`/users/${id}/password`, {
      method: 'POST',
      body: JSON.stringify({ new_password: newPassword }),
    }),
  deleteUser: (id: number) => request<void>(`/users/${id}`, { method: 'DELETE' }),

  /**
   * The hashed bundle name the server is currently serving.
   *
   * Not an /api call, and deliberately no backend involvement: nginx already
   * serves index.html as no-cache while /assets/ is immutable, so the document
   * always names the current build. A build id plumbed through the backend
   * would be a second source of truth that could disagree with what is
   * actually being served.
   *
   * Returns null when it cannot be determined -- in dev the document names
   * /src/main.tsx rather than a hashed file, and a failed request must never
   * be read as "a new version exists".
   */
  servedBundle: async (): Promise<string | null> => {
    const response = await fetch('/index.html', { cache: 'no-store', credentials: 'same-origin' })
    if (!response.ok) return null
    return bundleNameIn(await response.text())
  },

  /** The action vocabulary, so the filter never holds a stale copy of the enum. */
  listAuditActions: () => request<string[]>('/audit/actions'),

  listAudit: (filter: AuditFilter = {}) => {
    const query = new URLSearchParams()
    // Repeated, not comma-joined: FastAPI reads a list from repeated keys.
    for (const action of filter.actions ?? []) query.append('action', action)
    if (filter.username) query.set('username', filter.username)
    if (filter.since) query.set('since', filter.since)
    if (filter.until) query.set('until', filter.until)
    if (filter.limit !== undefined) query.set('limit', String(filter.limit))
    if (filter.offset) query.set('offset', String(filter.offset))
    const suffix = query.toString()
    return request<AuditPage>(`/audit${suffix ? `?${suffix}` : ''}`)
  },
}
